import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import * as passkeyApi from 'darwin_core/api/passkeys';
import {
  describePasskey,
  formatPasskeyDate,
  MAX_PASSKEYS,
  needsSignIn,
  PASSKEY_ERRORS,
  passkeyApiProblem,
} from 'darwin_core/passkeys';

import SignOutButton from '../auth/SignOutButton.jsx';
import ModalShell from '../components/ModalShell.jsx';
import Bone from '../components/Skeleton.jsx';
import { PasskeyIcon, SpinnerIcon } from '../components/icons.jsx';
import ScreenHeader from '../components/ScreenHeader.jsx';
import { ACCOUNT_PATH } from '../navigation/paths.js';
import {
  addPasskey,
  passkeyAlreadyAdded,
  passkeyCancelled,
  passkeysSupported,
} from '../passkeys/webauthn.js';

// The Passkeys page, under Account: the account's passkeys, each with when it
// was added and last used and a way to remove it, plus adding one in this
// browser.
//
// Adding or removing one takes a few round trips (and, to add, the browser's
// own prompt), so the page says what it's doing at each stage, in the control
// used and in the status line under Add, and puts a failure beside the control
// that was used, so nothing above it moves.

const LOAD_FAILED = 'Your passkeys could not be loaded.';
const ADDED_BUT_LOAD_FAILED =
  'The passkey was added, but your passkeys could not be reloaded.';
const REMOVED_BUT_LOAD_FAILED =
  'The passkey was removed, but your passkeys could not be reloaded.';
const REMOVE_FAILED =
  'The passkey could not be removed. Please try again later.';

const SIGN_IN_AGAIN =
  'Your session has ended. Log out and log in again, then try again.';
const RESET_SINCE_SIGN_IN =
  'Your password was reset after you logged in here. Log out and log in again with your new password.';

const signInAgainMessage = (error) =>
  passkeyApiProblem(error) === 'signedInBeforeReset'
    ? RESET_SINCE_SIGN_IN
    : SIGN_IN_AGAIN;

// What to say when adding a passkey fails. A cancelled prompt is not a failure
// and never reaches here.
function addFailedMessage(error) {
  switch (passkeyApiProblem(error)) {
    case 'limitReached':
      return `You can have up to ${MAX_PASSKEYS} passkeys. Remove one you no longer use, then try again.`;
    case 'recentSignInRequired':
      return 'For your security, adding a passkey needs a recent sign-in. Log out, log in again, then add your passkey straight away.';
    case 'signedInBeforeReset':
    case 'sessionEnded':
      return signInAgainMessage(error);
    default:
      break;
  }
  if (passkeyAlreadyAdded(error)) {
    return 'This browser already has a passkey for your Halo account.';
  }
  return 'A passkey could not be added. Please try again later.';
}

// What the confirmation before removing `passkey` says. The app's says the
// same, of a phone.
function removeDescription(passkey) {
  const { title, inlineDetail, noun, provider, legacy } =
    describePasskey(passkey);
  const stopped = legacy
    ? `You won’t be able to sign in to Halo with “${title}” (${inlineDetail}) any more.`
    : `You won’t be able to sign in to Halo with the ${noun} (${inlineDetail}) any more.`;
  return `${stopped} A device already signed in with it stays signed in, and the passkey stays in ${
    provider ?? 'its password manager'
  } until you delete it there.`;
}

// Oldest first, so a passkey just added is always last, beside Add. The API
// lists them by credential ID, which puts a new one anywhere; its order breaks
// a tie (the sort is stable).
const oldestFirst = (passkeys) =>
  [...passkeys].sort(
    (a, b) => (a.CreatedAt ?? Infinity) - (b.CreatedAt ?? Infinity),
  );

const loadError = (ex, message = LOAD_FAILED) => ({
  at: 'load',
  message: needsSignIn(ex) ? signInAgainMessage(ex) : message,
  signOut: needsSignIn(ex),
});

const ADDING = 'Adding a passkey…';
const SAVING = 'Saving your passkey…';
const REMOVING = 'Removing the passkey…';

export default function PasskeysScreen() {
  const [supported] = useState(passkeysSupported);
  // null until the first load finishes
  const [passkeys, setPasskeys] = useState(null);
  // When a password reset last removed the account's passkeys, if none has
  // been added since.
  const [removedByResetAt, setRemovedByResetAt] = useState(null);
  // The latest load failed, so the list on hand can't be trusted: show the error
  // alone rather than an empty list (or one a removal has just changed).
  const [loadFailed, setLoadFailed] = useState(false);
  // What's running: { add: 'prompt' } while the browser's prompt is up,
  // { add: 'saving' } once it has made the passkey, or { remove: id }.
  const [busy, setBusy] = useState(null);
  // { at, message, signOut }: shown beside what failed (`at`): 'load' (the
  // list), 'add' (Add) or a passkey's id (its row), with Log out when only
  // signing in again can fix it.
  const [error, setError] = useState(null);
  // What's under way or just done, in the status line under Add.
  const [status, setStatus] = useState('');
  const [toRemove, setToRemove] = useState(null);
  // Where focus goes once the screen has re-rendered after an action, and an
  // error to show once it has: { targets, from, error }. `targets` are tried in
  // turn ('retry', 'add', 'heading', 'last' or a passkey's id) for the first on
  // screen. Focus stays where it is if the user has moved it on from `from`,
  // the control the action started from.
  const [after, setAfter] = useState(null);
  // Held while an add or a removal runs, so only one runs at a time. A fast
  // double-click lands before React re-renders, and a second add would abort
  // the browser's first passkey prompt and may be refused, a second removal
  // deletes twice. The buttons stay enabled while busy (aria-disabled), so the
  // one in use keeps focus, and this is what makes them do nothing. The app's
  // Passkeys screen does the same.
  const actionRef = useRef(false);
  const retryRef = useRef(null);
  const addRef = useRef(null);
  const removeRefs = useRef(new Map());

  // A layout effect, so focus moves in the same task as the change that took
  // it (the removed row going, say), rather than resting on the page between.
  useLayoutEffect(() => {
    if (!after) {
      return;
    }
    setAfter(null);
    const active = document.activeElement;
    const movedOn =
      active &&
      active !== document.body &&
      active.isConnected &&
      active !== after.from;
    if (!movedOn) {
      const find = (target) =>
        target === 'retry'
          ? retryRef.current
          : target === 'add'
            ? addRef.current
            : target === 'heading'
              ? document.getElementById('main-content')
              : target === 'last'
                ? [...removeRefs.current.values()].at(-1)
                : removeRefs.current.get(target);
      const target =
        after.targets.map(find).find((element) => element?.isConnected) ??
        document.getElementById('main-content');
      if (target !== active) {
        target?.focus();
      }
    }
    // After the focus move, not with it: a screen reader announcing the newly
    // focused control can cut an alert that lands at the same moment, and the
    // alert matters more.
    if (after.error) {
      setError(after.error);
    }
  }, [after]);

  // Resolves to { ok } once the list has loaded, or { ok: false, ex }.
  const load = useCallback(async () => {
    try {
      const listed = await passkeyApi.listPasskeys();
      setPasskeys(oldestFirst(listed.passkeys));
      setRemovedByResetAt(listed.removedByPasswordResetAt);
      setLoadFailed(false);
      return { ok: true };
    } catch (ex) {
      setLoadFailed(true);
      return { ok: false, ex };
    }
  }, []);

  useEffect(() => {
    load().then((loaded) => {
      if (!loaded.ok) {
        setError(loadError(loaded.ex));
      }
    });
  }, [load]);

  async function handleRetry() {
    setError(null);
    setLoadFailed(false);
    setPasskeys(null);
    const loaded = await load();
    // To the top of the page once the list is back; back to Try again if not.
    setAfter(
      loaded.ok
        ? { targets: ['heading'], from: null }
        : { targets: ['retry'], from: null, error: loadError(loaded.ex) },
    );
  }

  async function handleAdd() {
    if (actionRef.current) {
      return;
    }
    actionRef.current = true;
    const from = addRef.current;
    setError(null);
    setStatus(ADDING);
    setBusy({ add: 'prompt' });
    try {
      await addPasskey({
        onPrompted: () => {
          setBusy({ add: 'saving' });
          setStatus(SAVING);
        },
      });
      const loaded = await load();
      if (loaded.ok) {
        setStatus('Passkey added.');
        // Focus stays on Add; at the limit Add has gone, so to the passkey
        // just added, which is last.
        setAfter({ targets: ['add', 'last'], from });
      } else {
        // The add worked, so say so, or the user may add it again.
        setStatus('');
        setAfter({
          targets: ['retry'],
          from,
          error: loadError(loaded.ex, ADDED_BUT_LOAD_FAILED),
        });
      }
    } catch (ex) {
      setStatus('');
      if (!passkeyCancelled(ex)) {
        setAfter({
          targets: ['add'],
          from,
          error: {
            at: 'add',
            message: addFailedMessage(ex),
            signOut: needsSignIn(ex),
          },
        });
      }
    } finally {
      actionRef.current = false;
      setBusy(null);
    }
  }

  async function handleRemove() {
    const passkey = toRemove;
    if (actionRef.current || !passkey) {
      return;
    }
    actionRef.current = true;
    // Closing the confirm hands focus back to this row's Remove button, which
    // keeps it while the removal runs. Once the passkey has gone, focus goes
    // to the next row's Remove (else the one before, else Add), where the
    // eye already is, rather than to the top of the page.
    const from = removeRefs.current.get(passkey.id);
    const ids = passkeys.map(({ id }) => id);
    const at = ids.indexOf(passkey.id);
    const neighbours = [ids[at + 1], ids[at - 1]].filter(Boolean);
    setToRemove(null);
    setError(null);
    setStatus(REMOVING);
    setBusy({ remove: passkey.id });
    try {
      try {
        await passkeyApi.deletePasskey(passkey.id);
      } catch (ex) {
        // Already removed, in another tab or on another device. It's gone
        // either way, so reload the list rather than report a failure that
        // every retry would repeat.
        if (ex?.code !== PASSKEY_ERRORS.NOT_FOUND) {
          throw ex;
        }
      }
      const loaded = await load();
      if (loaded.ok) {
        setStatus('Passkey removed.');
        setAfter({ targets: [...neighbours, 'add', 'heading'], from });
      } else {
        // One message: the assertive alert would cut off a polite "removed".
        setStatus('');
        setAfter({
          targets: ['retry'],
          from,
          error: loadError(loaded.ex, REMOVED_BUT_LOAD_FAILED),
        });
      }
    } catch (ex) {
      setStatus('');
      setAfter({
        targets: [passkey.id],
        from,
        error: {
          at: passkey.id,
          message: needsSignIn(ex) ? signInAgainMessage(ex) : REMOVE_FAILED,
          signOut: needsSignIn(ex),
        },
      });
    } finally {
      actionRef.current = false;
      setBusy(null);
    }
  }

  // The error for `at`, in a live region that's always there (so assistive
  // tech has it before an error lands) and collapses while empty. The control
  // it's beside names it as its description while it holds one (describedBy).
  const errorFor = (at, id) => (
    <>
      <p id={id} className="form-error" role="alert">
        {error?.at === at ? error.message : null}
      </p>
      {error?.at === at && error.signOut ? <SignOutButton /> : null}
    </>
  );

  const describedBy = (at, id, ...others) =>
    [...others, error?.at === at ? id : null].filter(Boolean).join(' ') ||
    undefined;

  const atLimit = (passkeys?.length ?? 0) >= MAX_PASSKEYS;
  const removing = busy?.remove ?? null;

  return (
    <main className="app-shell screen passkeys">
      <ScreenHeader
        title="Passkeys"
        sub="Sign in without your password"
        backTo={ACCOUNT_PATH}
        backLabel="Account"
      />
      <p className="passkeys__note">
        Use your fingerprint, face or device PIN to sign in to Halo instead of
        typing your password.
      </p>
      {supported ? null : (
        <p className="passkeys__note">
          Passkeys aren’t supported in this browser.
        </p>
      )}

      {errorFor('load', 'passkeys-load-error')}
      {loadFailed ? (
        <button
          ref={retryRef}
          type="button"
          className="passkeys__add"
          aria-describedby={describedBy('load', 'passkeys-load-error')}
          onClick={handleRetry}
        >
          Try again
        </button>
      ) : passkeys === null ? (
        // Rows' shapes while the list is on its way.
        <div className="passkeys__list" aria-busy="true">
          <p className="visually-hidden">Loading your passkeys…</p>
          {[0, 1].map((row) => (
            <div key={row} className="passkeys__row" aria-hidden="true">
              <Bone className="passkeys__icon-bone" />
              <span className="passkeys__row-text">
                <Bone className="skeleton--line skeleton--title" />
                <Bone className="skeleton--line" />
              </span>
            </div>
          ))}
        </div>
      ) : passkeys.length === 0 ? (
        removedByResetAt ? (
          // Said outright, so passkeys vanishing after a reset doesn't look
          // like a bug.
          <p className="passkeys__note">
            Your passkeys were removed when your password was reset on{' '}
            {formatPasskeyDate(removedByResetAt)}. Add a passkey again to sign
            in without your password.
          </p>
        ) : (
          <p className="passkeys__note">You don’t have any passkeys.</p>
        )
      ) : (
        <ul
          className="passkeys__list"
          aria-label="Your passkeys"
          aria-busy={removing ? 'true' : undefined}
        >
          {passkeys.map((passkey) => {
            const { title, detail, noun, legacy } = describePasskey(passkey);
            const name = legacy ? `passkey ${title}` : noun;
            const isRemoving = removing === passkey.id;
            return (
              <li key={passkey.id} className="passkeys__row">
                {/* The same for every passkey: there's no icon for each
                    password manager. */}
                <span className="passkeys__icon" aria-hidden="true">
                  <PasskeyIcon />
                </span>
                <div className="passkeys__row-text">
                  <span className="passkeys__name">{title}</span>
                  <span
                    id={`passkey-use-${passkey.id}`}
                    className="passkeys__use"
                  >
                    {detail}
                  </span>
                  {errorFor(passkey.id, `passkey-error-${passkey.id}`)}
                </div>
                <button
                  type="button"
                  className="link-button passkeys__remove"
                  aria-label={
                    isRemoving ? `Removing ${name}` : `Remove ${name}`
                  }
                  // Two can share a title (the same password manager, or the
                  // same browser); the second line tells them apart.
                  aria-describedby={describedBy(
                    passkey.id,
                    `passkey-error-${passkey.id}`,
                    `passkey-use-${passkey.id}`,
                  )}
                  aria-disabled={busy ? 'true' : undefined}
                  ref={(button) => {
                    if (button) {
                      removeRefs.current.set(passkey.id, button);
                    } else {
                      removeRefs.current.delete(passkey.id);
                    }
                  }}
                  onClick={() => {
                    if (!actionRef.current) {
                      setToRemove(passkey);
                    }
                  }}
                >
                  {isRemoving ? 'Removing…' : 'Remove'}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {supported && passkeys !== null && !loadFailed ? (
        atLimit ? (
          <p className="passkeys__note">
            You have {MAX_PASSKEYS} passkeys, the most an account can have.
            Remove one you no longer use to add another.
          </p>
        ) : (
          <button
            ref={addRef}
            type="button"
            className="passkeys__add"
            aria-describedby={describedBy('add', 'passkey-add-error')}
            aria-disabled={busy ? 'true' : undefined}
            onClick={handleAdd}
          >
            {busy?.add ? (
              <>
                <SpinnerIcon />
                {busy.add === 'prompt' ? 'Adding…' : 'Saving…'}
              </>
            ) : (
              'Add a passkey'
            )}
          </button>
        )
      ) : null}
      {errorFor('add', 'passkey-add-error')}
      {/* Its line is kept, empty or not, so nothing moves when it speaks. It's
          empty while an error shows, so the error sits right under Add. */}
      <p className="passkeys__status" role="status">
        {status}
      </p>

      {toRemove ? (
        <ModalShell
          labelledBy="passkey-remove-title"
          className="schedule-modal--confirm"
          onClose={() => setToRemove(null)}
        >
          <h2 id="passkey-remove-title" className="schedule-modal__title">
            Remove passkey?
          </h2>
          {/* Removing a passkey only stops new sign-ins with it. Once there's
              a way to sign out everywhere, link to it here for a device that
              shouldn't stay signed in. */}
          <p className="schedule-modal__desc">{removeDescription(toRemove)}</p>
          <div className="schedule-modal__actions">
            <button
              type="button"
              className="schedule-modal__cancel"
              onClick={() => setToRemove(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={handleRemove}
            >
              Remove
            </button>
          </div>
        </ModalShell>
      ) : null}
    </main>
  );
}
